import { useState, useCallback, useEffect, useRef } from 'react';
import { getAssets, getAssetsCount } from '@/endpoints/assets';
import { requestScopeFor } from '@/session/scopedTransport';
import { useCommittedOrganization } from '@/session/OrganizationIdentityContext';
import { acquireOperationContext, isSessionTransition, operationRequirement } from '@/session/operation';
import { Asset, UseAssetsReturn } from '@/types/asset';
import { utils } from '@/utils';

export const useAssets = (): UseAssetsReturn => {
  const organization = useCommittedOrganization();
  const [assets, setAssets] = useState<Asset[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [total, setTotal] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const requestSequenceRef = useRef(0);
  const requestControllerRef = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      requestSequenceRef.current += 1;
      requestControllerRef.current?.abort();
    },
    []
  );

  const fetchAssets = useCallback(
    async (page: number, rowsPerPage: number): Promise<void> => {
      requestControllerRef.current?.abort();
      const controller = new AbortController();
      requestControllerRef.current = controller;
      const sequence = (requestSequenceRef.current += 1);
      const operation = acquireOperationContext(organization);
      if (!operation.isScopeCurrent()) return;
      const canPublish = () => sequence === requestSequenceRef.current && !controller.signal.aborted && operation.isScopeCurrent();
      const options = {
        signal: controller.signal,
        operationRequirement: operationRequirement(operation, 'scope'),
      };

      setLoading(true);
      setError(null);

      try {
        const countResponse = await getAssetsCount(requestScopeFor(operation.organization), { limit: 1000 }, options);
        if (!canPublish() || utils.error.isCanceledResponse(countResponse)) return;
        if (countResponse.isError) {
          setError(countResponse.errors?.error ?? 'Failed to fetch asset count');
          setAssets([]);
          setTotal(0);
          return;
        }
        if (countResponse.result) setTotal(countResponse.result.count);

        const assetsResponse = await getAssets(requestScopeFor(operation.organization), { limit: rowsPerPage, page: page + 1 }, options);
        if (!canPublish() || utils.error.isCanceledResponse(assetsResponse)) return;
        if (assetsResponse.isError) {
          setError(assetsResponse.errors?.error ?? 'Failed to fetch assets');
          setAssets([]);
          return;
        }

        const processedAssets = (assetsResponse.result ?? []).map((asset: Asset) => {
          if (!asset.file_ext && asset.filename) {
            const pieces = asset.filename.split('.');
            return {
              ...asset,
              file_ext: pieces.length > 1 ? pieces[pieces.length - 1] : '',
            };
          }
          return asset;
        });
        setAssets(processedAssets);
      } catch (caught) {
        if (!canPublish() || isSessionTransition(caught)) return;
        setError(utils.error.getErrorMessage(caught, 'Failed to fetch assets'));
        setAssets([]);
      } finally {
        if (canPublish()) setLoading(false);
        if (requestControllerRef.current === controller) requestControllerRef.current = null;
      }
    },
    [organization]
  );

  return {
    assets,
    loading,
    total,
    error,
    fetchAssets,
  };
};
